import { useEffect, useRef } from 'react';

/**
 * Overlays currently listening for Escape, oldest first.
 *
 * Every overlay used to listen on the window itself, so one Escape dismissed
 * the whole stack: a drawer opened behind a dialog vanished along with it, and
 * the drawer looked like it had opened and closed on its own. Only the overlay
 * on top answers the key now, and the rest wait their turn.
 */
const escapeStack: symbol[] = [];

/** Closes this overlay on Escape, but only while it is the topmost one. */
export function useDismissOnEscape(active: boolean, onClose: () => void) {
  // Held in a ref so a new closure on every render does not re-register the
  // overlay, which would shuffle it to the top of the stack out of turn.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!active) return;
    const id = Symbol('overlay');
    escapeStack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && escapeStack[escapeStack.length - 1] === id) close.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      escapeStack.splice(escapeStack.indexOf(id), 1);
    };
  }, [active]);
}
